import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImageUpload } from '../hooks/useImageUpload';
import { usePhotoUrl } from '../hooks/usePhotoUrl';
import { Upload, X, CheckCircle, AlertCircle } from 'lucide-react';

export default function StorageTest() {
  const { user } = useAuth();
  const { upload, uploading, progress, error } = useImageUpload();
  const [uploadedPath, setUploadedPath] = useState(null);
  const { url, loading: urlLoading } = usePhotoUrl(uploadedPath);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Test metadata
    const metadata = {
      inspectionId: 'test-inspection-123',
      itemId: 'test-item-456',
    };

    const result = await upload(file, metadata);

    if (result.path) {
      setUploadedPath(result.path);
      console.log('✅ Upload successful!');
      console.log('Path:', result.path);
      console.log('URL:', result.url);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4">🧪 Storage Test</h2>

        {/* User Info */}
        <div className="mb-4 p-4 bg-blue-50 rounded">
          <p className="text-sm text-gray-600">Zalogowany jako:</p>
          <p className="font-mono text-sm">{user?.email}</p>
          <p className="font-mono text-xs text-gray-500">ID: {user?.id}</p>
        </div>

        {/* Upload Section */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            1. Wybierz zdjęcie do uploadu
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
          />
        </div>

        {/* Progress */}
        {uploading && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Uploading...</span>
              <span className="text-sm font-medium text-blue-600">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Upload Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Success */}
        {uploadedPath && !error && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
            <div className="flex items-start mb-2">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-green-800">Upload Successful! ✅</p>
                <p className="text-sm text-gray-600 mt-1">Storage path:</p>
                <code className="text-xs bg-white px-2 py-1 rounded block mt-1 break-all">
                  {uploadedPath}
                </code>
              </div>
            </div>
          </div>
        )}

        {/* Display uploaded photo */}
        {uploadedPath && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              2. Zdjęcie z Storage (Signed URL):
            </h3>

            {urlLoading ? (
              <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : url ? (
              <div>
                <img
                  src={url}
                  alt="Uploaded"
                  className="max-w-full h-auto rounded shadow-lg"
                />
                <div className="mt-2 p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-600">Signed URL (ważny 1h):</p>
                  <code className="text-xs break-all">{url}</code>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 rounded">
                <p className="text-sm text-red-600">Nie udało się pobrać URL</p>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-gray-50 rounded">
          <h4 className="font-medium text-gray-900 mb-2">✅ Test checklist:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>1. Wybierz zdjęcie</li>
            <li>2. Sprawdź czy progress bar działa</li>
            <li>3. Sprawdź czy pojawił się zielony komunikat sukcesu</li>
            <li>4. Sprawdź czy zdjęcie się wyświetla</li>
            <li>5. Otwórz Console (F12) i sprawdź logi</li>
            <li>6. Sprawdź w Supabase Dashboard → Storage czy plik jest widoczny</li>
          </ul>
        </div>

        {/* Debug Info */}
        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            🐛 Debug Info (kliknij aby rozwinąć)
          </summary>
          <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono">
            <p>User ID: {user?.id}</p>
            <p>Uploaded Path: {uploadedPath || 'none'}</p>
            <p>Signed URL: {url ? 'generated ✅' : 'not yet'}</p>
            <p>URL Loading: {urlLoading ? 'yes' : 'no'}</p>
            <p>Upload Error: {error || 'none'}</p>
          </div>
        </details>
      </div>
    </div>
  );
}
